import type { MediaTransportProfile } from './delivery-assignment.js';
import { parseMediaTransportProfile } from './renditions.js';

/** Structural runtime view of canonical gaming/v1 PrivateMediaAuthority, not a public ABR rendition. */
export interface PrivateMediaAuthority {
  namespace: 'reaction';
  sessionId: string;
  publicationId: string;
  medium: 'camera' | 'microphone';
  consentEpoch: number;
  generation: string;
  keyId: string;
  initSha256: string;
  codecs: string[];
  protocolVersion: 2;
  expiresAt: string;
  transportProfile: MediaTransportProfile;
}
const keys = new Set(['namespace','sessionId','publicationId','medium','consentEpoch','generation','keyId','initSha256','codecs','protocolVersion','expiresAt','transportProfile']);
const identifier = (v: unknown): v is string => typeof v === 'string' && v.length >= 1 && v.length <= 128;

/** Shape and identity equality only. The trusted issuer must establish consent, freshness, init and key ownership. */
export function parsePrivateMediaAuthority(value: unknown): PrivateMediaAuthority | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const a = value as Record<string, unknown>;
  if (Object.keys(a).some(key => !keys.has(key)) || a.namespace !== 'reaction' || !identifier(a.sessionId) || !identifier(a.publicationId) ||
    (a.medium !== 'camera' && a.medium !== 'microphone') || !Number.isSafeInteger(a.consentEpoch) || (a.consentEpoch as number) < 1 ||
    !identifier(a.generation) || !/^[A-Za-z0-9_-]+$/.test(a.generation) || a.keyId !== a.generation || a.protocolVersion !== 2 ||
    typeof a.initSha256 !== 'string' || !/^[a-f0-9]{64}$/.test(a.initSha256) || !Array.isArray(a.codecs) || a.codecs.length !== 1 ||
    typeof a.codecs[0] !== 'string' || (a.medium === 'camera' ? !/^avc1\.[0-9A-Fa-f]{6}$/.test(a.codecs[0]) : a.codecs[0] !== 'mp4a.40.2') ||
    typeof a.expiresAt !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/i.test(a.expiresAt) || !Number.isFinite(Date.parse(a.expiresAt))) return null;
  const transportProfile = parseMediaTransportProfile(a.transportProfile);
  if (!transportProfile) return null;
  return {namespace:'reaction', sessionId:a.sessionId, publicationId:a.publicationId, medium:a.medium, consentEpoch:a.consentEpoch as number,
    generation:a.generation, keyId:a.generation, initSha256:a.initSha256, codecs:[...a.codecs], protocolVersion:2, expiresAt:a.expiresAt, transportProfile};
}
